import { toPng } from 'html-to-image';

/**
 * Export a rendered DOM node as a PNG and hand the user a *real file*.
 *
 * Why not `window.print()`? In the LINE/Telegram in-app browsers most learners
 * use, `print()` is a silent no-op, and on mobile browsers the
 * print → "Save as PDF" path is unreliable — the user taps and nothing happens.
 * Generating a PNG in-JS works everywhere; we then deliver it via the most
 * capable channel the device offers:
 *   1. Web Share API with a file  → mobile (routes to "Save to Photos"/share sheet)
 *   2. `<a download>`             → desktop + Android Chrome
 *
 * The node must be *rendered* (laid out) — `display:none` nodes have no geometry
 * for html-to-image to capture; render the capture source off-screen instead.
 */
export async function exportNodeAsPng(
  node: HTMLElement,
  filename: string,
): Promise<'shared' | 'downloaded'> {
  // Wait for web fonts (Sarabun/IBM Plex) so text isn't captured as fallback/blank.
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* fonts API unavailable — proceed anyway */
    }
  }

  // pixelRatio 2 → crisp on retina/print; cacheBust dodges stale font/QR caches.
  const opts = { pixelRatio: 2, cacheBust: true, backgroundColor: '#ffffff' };

  // iOS/LINE WebViews paint embedded resources (fonts, the QR <svg>) lazily, so
  // the FIRST rasterise frequently returns blank. Render once as a warm-up to
  // force the paint, then keep the second result.
  await toPng(node, opts);
  const dataUrl = await toPng(node, opts);

  const file = await dataUrlToFile(dataUrl, filename);

  // Mobile-first: a native share sheet is the only reliable "save" inside
  // LINE/iOS WebViews, where anchor downloads are frequently ignored.
  if (file && typeof navigator !== 'undefined' && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: filename.replace(/\.png$/i, '') });
      return 'shared';
    } catch (err) {
      // User dismissed the sheet — that's a completed interaction, not a failure;
      // don't also fire a download. Any other error → fall through to download.
      if (err instanceof DOMException && err.name === 'AbortError') return 'shared';
    }
  }

  triggerDownload(dataUrl, filename);
  return 'downloaded';
}

async function dataUrlToFile(dataUrl: string, filename: string): Promise<File | null> {
  try {
    const blob = await (await fetch(dataUrl)).blob();
    return new File([blob], filename, { type: 'image/png' });
  } catch {
    return null;
  }
}

function triggerDownload(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
